import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";

import styles from "../pages/LiveTapPage.module.scss";

// How long the panel's contents outlive a close, so it slides out with its
// text still in it instead of emptying first. Must cover the slide in
// LiveTapPage.module.scss (.sidePanel 0.35s + .sidePanelInner 0.08s delay); the
// transcript is big enough to be worth unmounting once it's actually hidden.
const PANEL_EXIT_MS = 400;

/**
 * A live canvas beside its info panel (TT-972): the canvas keeps the row,
 * the panel slides in from the right (or stacks under it on narrow screens)
 * and its contents outlive a close for the length of the slide. One row
 * for every live system (TT-1168 note 5: the scene board gets the same
 * transcript classic has), so the two can never drift apart.
 *
 * `infoOpen` is the caller's — its toolbar toggles it. While the canvas is
 * fullscreen the panel moves to the gutter the canvas reserves beside
 * itself (TT-1174 note 3): `children` is a render prop given `{ aside,
 * onFullscreenChange }` — the panel on its surface while it is open, to
 * hand the canvas (or stage) for that gutter, and the report of fullscreen
 * state that stands the inline placement down.
 */
export default function SplitStage({ infoOpen, panel, children }) {
  // Hold the panel's contents through the close animation.
  const [showContent, setShowContent] = useState(false);
  const timerRef = useRef();
  useEffect(() => {
    clearTimeout(timerRef.current);
    if (infoOpen) {
      setShowContent(true);
    } else {
      timerRef.current = setTimeout(() => setShowContent(false), PANEL_EXIT_MS);
    }
    return () => clearTimeout(timerRef.current);
  }, [infoOpen]);

  const [fullscreen, setFullscreen] = useState(false);
  const aside = infoOpen ? <div className={styles.fullscreenAside}>{panel}</div> : null;

  return (
    <div className={styles.liveStage}>
      <div className={classNames(styles.canvasSlot, { [styles.canvasSlotSplit]: infoOpen })}>
        {children({ aside, onFullscreenChange: setFullscreen })}
      </div>
      {!fullscreen && (
        <div className={classNames(styles.sidePanel, { [styles.sidePanelOpen]: infoOpen })}>
          <div className={classNames(styles.sidePanelInner, { [styles.sidePanelInnerOpen]: infoOpen })}>
            {showContent && panel}
          </div>
        </div>
      )}
    </div>
  );
}

SplitStage.propTypes = {
  infoOpen: PropTypes.bool,
  panel: PropTypes.node,
  // ({ aside, onFullscreenChange }) => the canvas or stage for the row.
  children: PropTypes.func.isRequired,
};
